import clsx from 'clsx';
import { Typography } from '../typography';
import { CaseStatusFilter } from '@/interfaces';
import { Check, List, X } from 'lucide-react';
import { END_STATUSES_CASE } from '@/const/cases';
import { TooltipComponent } from '../tooltip';
import Image from 'next/image';
import Icons from '@/const/icons';

interface Props {
	status: CaseStatusFilter;
	withLabel?: boolean;
	showDot?: boolean;
}

export const LabelState: React.FC<Props> = ({
	status,
	withLabel = true,
	showDot = true,
}) => {
	// Segun el diseño de figma algunos CaseStatus se tienen que visualizar en una solo linea
	// por lo que se necesita un autoWidth en el tooltip
	// a demas algunos status necesitan el autoWidth y un salto de linea para cumplir con el diseño, ver la variable dir como ejemplo
	const needsAutoWidth = (status: CaseStatusFilter): boolean => {
		const autoWidthStatuses: CaseStatusFilter[] = [
			'Opt Out Request Declined',
			'Settlement Declined',
			'Case Aborted',
			'Declined',
			'Cancelled',
		];
		return autoWidthStatuses.includes(status);
	};

	// A changed was made in April 25th 2025
	// the tooltip content now uses !w-auto
	// this means you have to insert a line feed (salto de linea) to match the text with the figma design
	// path to the component that implements !w-auto
	// claimmagic-frontend/components/ui/tooltip.tsx
	const dir = {
		'In Progress':
			'The case is currently being worked \non and is moving forward.',
		'Action Required':
			'Immediate attention is needed; the \ncase is awaiting your action.',
		'On Hold':
			'Work on the case is temporarily \npaused; awaiting your action.',
		'Resolved Cases':
			'The case has been successfully \ncompleted and is now closed.',
		Declined: 'You have not been\n approved for the case.',
		'Case Unsuccessful':
			'The outcome of the case was \nunsuccessful, and it has been closed.',
		'Settlement Declined': 'The proposed settlement \nwas rejected by you.',
		Cancelled: 'The case has been cancelled\nand will not proceed further.',
		'Resolved Case':
			'The case has been successfully \ncompleted and is now closed.',
		'Case Aborted': 'Lawyer will not pursue the case.',
		'Opt Out Request Declined': 'The opt out request was rejected by you.',
	};

	const getLabelStatus = () => {
		if (status === 'Case Aborted' || status === 'Aborted') {
			return 'Case Has Ended';
		}
		return status;
	};

	return (
		<div className={clsx('flex items-center', { 'gap-2': showDot })}>
			<TooltipComponent
				className="w-60 justify-center"
				label={dir[status as keyof typeof dir]}
				autoWidth={needsAutoWidth(status)}
				trigger={
					<div>
						<Check
							size={16}
							color="#40bb71"
							className={clsx({
								hidden: status !== 'Resolved Case',
							})}
						/>

						<X
							size={16}
							color="#FF0043"
							className={clsx({
								hidden: !END_STATUSES_CASE.includes(status),
							})}
						/>

						<List
							size={16}
							color="#8039DF"
							className={clsx({
								hidden: status !== 'View All',
							})}
						/>
						<Image
							alt="alphabetical order"
							src={Icons.alphabeticalOrder}
							width={16}
							height={16}
							className={clsx('text-primary', {
								hidden: status !== 'Alphabetical Order',
							})}
							unoptimized
						/>
						{showDot && (
							<div
								className={clsx(
									'flex items-center justify-center rounded-full w-3 h-3',
									{
										'green-gradient': status === 'In Progress',
										'red-gradient':
											status === 'Action Required' ||
											status === 'Applicant Intake',
										'gray-gradient': status === 'Archived',
										'yellow-gradient': status === 'On Hold',
										hidden:
											status === 'Resolved Case' ||
											status === 'Alphabetical Order' ||
											status === 'View All' ||
											END_STATUSES_CASE.includes(status),
									}
								)}
							>
								<span
									className={clsx('w-2 h-2 rounded-full', {
										'bg-green-120': status === 'In Progress',
										'bg-main-red':
											status === 'Action Required' ||
											status === 'Applicant Intake',
										'bg-dark-40': status === 'Archived',
										'bg-yellow-base': status === 'On Hold',
									})}
								/>
							</div>
						)}
					</div>
				}
			/>
			{withLabel && (
				<Typography type="semibold-14" className="capitalize">
					{getLabelStatus()}
				</Typography>
			)}
		</div>
	);
};
